import { IndexedDbDocStore } from '@/doc-sync/indexeddb-doc-store';

import type { DiagramMeta, DiagramMetaExtra } from './diagram-store';
import { parseStoredDiagram, type StoredDiagram } from './stored-diagram';

export { isIndexedDbAvailable } from '@/doc-sync/indexeddb-doc-store';

/** Builds one gallery-list entry from a raw (unparsed, unvalidated) IndexedDB
 *  record, or `null` if the record is too corrupt to summarize. `list()` only
 *  needs the envelope's own fields — it deliberately does NOT run the full
 *  `parseStoredDiagram`/`migrate` pipeline (that's `load()`'s job) so one
 *  corrupt document can't throw and blank both galleries; it's just skipped.
 *  Exported for unit testing without a real IndexedDB. */
export function diagramMetaFromRecord(
	raw: unknown,
	thumbById: ReadonlyMap<string, Blob>,
): DiagramMeta | null {
	if (typeof raw !== 'object' || raw == null) return null;
	const d = raw as Record<string, unknown>;
	const kind = (d.pattern as Record<string, unknown> | null | undefined)?.kind;
	if (kind !== 'crochet' && kind !== 'knitting') return null;
	if (
		typeof d.id !== 'string'
		|| typeof d.name !== 'string'
		|| typeof d.createdAt !== 'number'
		|| typeof d.updatedAt !== 'number'
	) {
		return null;
	}
	return {
		id: d.id,
		name: d.name,
		createdAt: d.createdAt,
		updatedAt: d.updatedAt,
		thumbnail: thumbById.get(d.id) ?? null,
		kind,
		// Only the server archives; a row read from this device is never archived.
		archived: false,
	};
}

export class IndexedDbDiagramStore extends IndexedDbDocStore<StoredDiagram, DiagramMetaExtra> {
	/** @param dbName IndexedDB database name — defaults to the shared
	 *  account database. Callers that need an isolated namespace (e.g. the
	 *  /try editors' local-only store) pass a distinct name so the two
	 *  databases never share records. */
	constructor(dbName: string = 'azabu-diagrams') {
		super({
			dbName,
			// v3 (SP-52) adds the ydocs store; the /try database gets it too, unused.
			dbVersion: 3,
			yDocs: true,
			parse: parseStoredDiagram,
			metaFromRecord: diagramMetaFromRecord,
		});
	}
}
